/**
 * GT-UI-API-RATE-LIMIT-REQUEST-STORM-FIX-02 — global rate-limit notice.
 *
 * A tiny module-level pub/sub so the QueryClient cache (a plain
 * callback, outside React) can ask the UI to show one controlled "too
 * many requests" message when a 429 is observed. The notice is
 * **debounced**: a burst of queries that all 429 together produces a
 * single toast, not one per query — exactly the kind of stampede the
 * rest of this sprint removes. `<RateLimitToastBridge>` subscribes and
 * renders the toast.
 */

export type RateLimitNoticeListener = (retryAfterSeconds: number | null) => void;

let listener: RateLimitNoticeListener | null = null;
// `-Infinity` so the FIRST notice always fires regardless of clock value.
let suppressUntil = Number.NEGATIVE_INFINITY;

/**
 * Floor on the gap between notices (ms) when the server gives no / a tiny
 * `Retry-After`. Above this floor the gap is the actual Retry-After window.
 */
export const RATE_LIMIT_NOTICE_MIN_GAP_MS = 5_000;

/** Register the single UI listener. Pass `null` to unsubscribe. */
export function setRateLimitNoticeListener(next: RateLimitNoticeListener | null): void {
  listener = next;
}

/**
 * Emit a rate-limit notice, coalesced to **one toast per Retry-After
 * window**.
 *
 * GT-…-FIX-04 — the owner saw a cascade of toasts ("…try again in 31 s",
 * "…26 s", "…21 s") — one every ~5 s for the WHOLE limiter window. Now,
 * the first 429 of a window shows one toast and further 429s are
 * suppressed until that `Retry-After` window expires (floored at
 * {@link RATE_LIMIT_NOTICE_MIN_GAP_MS}), so a 30 s limiter window produces
 * a single message. `now` is injectable for tests. Returns `true` when the
 * notice actually fired.
 */
export function emitRateLimited(
  retryAfterSeconds: number | null,
  now: number = Date.now(),
): boolean {
  if (now < suppressUntil) return false;
  const windowMs =
    retryAfterSeconds != null && retryAfterSeconds > 0
      ? retryAfterSeconds * 1000
      : RATE_LIMIT_NOTICE_MIN_GAP_MS;
  suppressUntil = now + Math.max(windowMs, RATE_LIMIT_NOTICE_MIN_GAP_MS);
  listener?.(retryAfterSeconds);
  return true;
}

/** Test-only reset. */
export function __resetRateLimitNoticeForTests(): void {
  listener = null;
  suppressUntil = Number.NEGATIVE_INFINITY;
}
